import {
  fetchData,
  fileExceedSizeInMo,
  getImageUrl,
  IframelyMedia,
  isImageFile,
  mediaFromIframely,
  uploadFileToUrl,
} from '@prismamedia/one-components';
import { isRetryableError } from './retry';
import { GET_UPLOAD_URL } from '../apollo/queries/uploadUrl.image.graphql';
import { GET_RESOLVED_IMAGE } from '../apollo/queries/resolvedImage.image.graphql';
import { GET_FOCUS_POINT } from '../apollo/queries/focusPoint.image.graphql';
import { client } from '../apollo';
import {
  GetUploadUrlQuery,
  GetUploadUrlQueryVariables,
  GetResolvedImageQuery,
  GetResolvedImageQueryVariables,
  GetFocusPointQuery,
  GetFocusPointQueryVariables,
} from '../__generated__/queries-image';

const embedImage = (url: string) =>
  fetchData<IframelyMedia>(
    `${config.API_ONE_IFRAMELY}/iframely?uri=${encodeURIComponent(url)}`,
  );

const validateImageFile = (file: File): void => {
  if (fileExceedSizeInMo(file, 10)) {
    throw new Error('Le fichier fait plus de 10 Mo');
  }

  if (!isImageFile(file)) {
    throw new Error("Le fichier n'est pas une image");
  }
};

const uploadAndGetResolvedImageUrl = async (
  file: File,
  namespace: string,
): Promise<string> => {
  validateImageFile(file);

  // Get upload URL
  const { data: uploadUrlData } = await client.query<
    GetUploadUrlQuery,
    GetUploadUrlQueryVariables
  >({
    query: GET_UPLOAD_URL,
    variables: {
      namespace,
      originalName: file.name,
    },
  });

  const { key, uploadURL } = uploadUrlData.getUploadUrl;

  // Upload file
  await uploadFileToUrl(file, uploadURL);

  // Get resolved image URL with retry (S3 might have a delay before the image is accessible)
  let retryIteration = 0;

  const attemptGetResolvedImage = async (): Promise<GetResolvedImageQuery> => {
    retryIteration++;

    try {
      const result = await client.query<
        GetResolvedImageQuery,
        GetResolvedImageQueryVariables
      >({
        query: GET_RESOLVED_IMAGE,
        variables: { key },
      });

      if (!result.data?.getResolvedImage) {
        throw new Error('No resolved image data');
      }

      return result.data;
    } catch (error) {
      if (retryIteration >= 4) {
        throw error;
      }

      if (
        isRetryableError(error) ||
        (error instanceof Error &&
          error.message.includes('No resolved image data'))
      ) {
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * retryIteration),
        );
        return await attemptGetResolvedImage();
      }

      throw error;
    }
  };

  const resolvedImageData = await attemptGetResolvedImage();
  return resolvedImageData.getResolvedImage.url;
};

// Utility function for non-React contexts (can be called from regular functions)
export const uploadAndEmbedImage = async (
  file: File,
  namespace: string,
  { withFileName }: { withFileName?: boolean } = {},
) => {
  try {
    const url = await uploadAndGetResolvedImageUrl(file, namespace);

    // Embed image
    const iframely = await embedImage(url);

    // Get focus point
    let oneMedia = mediaFromIframely(iframely);
    const imageUrl = getImageUrl(oneMedia);
    const imageHeight = oneMedia.iframely.meta.height;
    const imageWidth = oneMedia.iframely.meta.width;

    try {
      const { data: focusPointData } = await client.query<
        GetFocusPointQuery,
        GetFocusPointQueryVariables
      >({
        query: GET_FOCUS_POINT,
        variables: {
          imageUrl,
          imageHeight,
          imageWidth,
        },
      });
      const { x, y, relativeX, relativeY } = focusPointData.getFocusPoint;
      oneMedia = { ...oneMedia, focusPoint: { x, y, relativeX, relativeY } };
    } catch (e) {}

    // Return iframely with optional fileName (for backward compatibility)
    return {
      ...iframely,
      ...(withFileName && { fileName: file.name }),
    };
  } catch (error) {
    throw new Error(
      `Erreur lors de l'importation du média "${file.name}": ${
        (error as Error)?.message
      }`,
    );
  }
};

// Helper function that returns just the URL (for postThumbnail)
export const uploadImage = async (file: File, namespace: string) => {
  try {
    const url = await uploadAndGetResolvedImageUrl(file, namespace);
    return { url };
  } catch (error) {
    throw new Error(
      `Erreur lors de l'importation du média "${file.name}": ${
        (error as Error)?.message
      }`,
    );
  }
};
